// src/pages/AgentChat.js - Complete file with custom modal implementation
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Alert, Badge, ListGroup, InputGroup } from 'react-bootstrap';
import { 
  FaRobot, 
  FaUser, 
  FaPaperPlane, 
  FaArrowLeft, 
  FaTasks, 
  FaPlay, 
  FaClock, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaHistory,
  FaPlus,
  FaComments,
  FaTrash,
  FaEdit,
  FaSearch,
  FaFilter,
  FaEye,
  FaEyeSlash,
  FaCog,
  FaChevronLeft,
  FaChevronRight,
  FaSync,
  FaExpand,
  FaCompress,
  FaTimes
} from 'react-icons/fa';
import Spinner from '../components/Spinner';
import ConversationSettingsModal from '../components/ConversationSettingsModal';
import { useAuth } from '../context/AuthContext';
import * as conversationApi from '../services/conversationApi';
import ConversationStorageService from '../services/conversationStorageService';
import './AgentChat.css';

// Custom Modal Component to avoid Bootstrap conflicts
const CustomModal = ({ show, onHide, title, children, footer, size = 'md' }) => {
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('custom-modal-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('custom-modal-open');
    }

    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('custom-modal-open');
    };
  }, [show]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onHide();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onHide();
    }
  };

  useEffect(() => {
    if (show) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [show]);

  if (!show) return null;

  return (
    <div className="custom-modal-overlay" onClick={handleBackdropClick}>
      <div className={`custom-modal-container custom-modal-${size}`}>
        <div className="custom-modal-header">
          <h4 className="custom-modal-title">{title}</h4>
          <button className="custom-modal-close" onClick={onHide}>
            <FaTimes />
          </button>
        </div>
        <div className="custom-modal-body">
          {children}
        </div>
        {footer && (
          <div className="custom-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

const AgentChat = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Main agent configuration
  const MAIN_AGENT = {
    id: 'Agent Developer',
    name: 'Agent Developer Assistant',
    description: 'Main AI agent for creating and managing other agents'
  };
  
  // Conversation management state
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [loadingConversations, setLoadingConversations] = useState(true);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Tasks state
  const [currentConversationTasks, setCurrentConversationTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskFilter, setTaskFilter] = useState('all');
  
  // UI state - Column layout management
  const [conversationsPanelExpanded, setConversationsPanelExpanded] = useState(true);
  const [conversationSearch, setConversationSearch] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  
  // Modal state
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [executingTask, setExecutingTask] = useState(false);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [newConversationTitle, setNewConversationTitle] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [taskExecutionResult, setTaskExecutionResult] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Auto-scroll function
  const scrollToBottom = (behavior = "smooth") => {
    if (messagesEndRef.current && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      
      if (isNearBottom || behavior === "auto") {
        messagesEndRef.current.scrollIntoView({ behavior });
      }
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversations on mount
  useEffect(() => {
    console.log('Component mounted, loading conversations...');
    loadConversationHistory();
    loadUserPreferences();
  }, [currentUser]);

  // Load messages and tasks when conversation changes
  useEffect(() => {
    if (currentConversationId) {
      console.log('Current conversation changed to:', currentConversationId);
      loadConversationMessages(currentConversationId);
      loadTasksForConversation(currentConversationId);
      ConversationStorageService.setActiveConversationId(currentConversationId);
      
      // Auto-collapse conversations panel when conversation is selected
      setConversationsPanelExpanded(false);
    } else {
      console.log('No current conversation selected');
      setMessages([]);
      setCurrentConversationTasks([]);
      ConversationStorageService.setActiveConversationId(null);
    }
  }, [currentConversationId]);

  // Focus on input when conversation changes
  useEffect(() => {
    if (currentConversationId && !showNewConversationModal && !showExecuteModal && !showSettingsModal) {
      setTimeout(() => messageInputRef.current?.focus(), 100);
    }
  }, [currentConversationId, showNewConversationModal, showExecuteModal, showSettingsModal]);

  // Auto-collapse when user starts typing
  const handleMessageInputFocus = () => {
    if (conversationsPanelExpanded) {
      setConversationsPanelExpanded(false);
    }
  };

  const loadUserPreferences = () => {
    const prefs = ConversationStorageService.getUserPreferences();
    console.log('Loading user preferences:', prefs);
    
    setTaskFilter(prefs.taskFilter || 'all');
    
    console.log('User preferences applied');
  };

  const loadConversationHistory = async () => {
    try {
      setLoadingConversations(true);
      console.log('Loading conversation history...');
      
      // Try to get conversations from server first
      let serverConversations = [];
      try {
        console.log('Attempting to fetch conversations from server...');
        const serverResponse = await conversationApi.getUserConversations();
        if (serverResponse && serverResponse.success && serverResponse.conversations) {
          serverConversations = serverResponse.conversations.map(conv => ({
            sessionId: conv.sessionId,
            title: conv.title || 'Untitled Conversation',
            createdAt: conv.createdAt,
            lastActivity: conv.updatedAt || conv.lastActivity || conv.createdAt,
            messageCount: parseInt(conv.messageCount) || 0,
            userId: conv.userId || currentUser?.username || currentUser?.email || 'anonymous',
            isActive: true
          }));
          console.log('Loaded conversations from server:', serverConversations.length);
        }
      } catch (serverError) {
        console.log('Could not fetch conversations from server:', serverError.message);
      }
      
      // Get local conversations
      const storedConversations = ConversationStorageService.getConversations();
      console.log('Raw stored conversations:', storedConversations);
      
      // Merge server and local conversations
      let mergedConversations = [];
      if (serverConversations.length > 0) {
        mergedConversations = serverConversations;
        const serverSessionIds = new Set(serverConversations.map(conv => conv.sessionId));
        const localOnly = storedConversations.filter(conv => !serverSessionIds.has(conv.sessionId));
        mergedConversations = [...mergedConversations, ...localOnly];
        ConversationStorageService.saveConversations(mergedConversations);
      } else {
        mergedConversations = storedConversations || [];
      }
      
      if (mergedConversations.length === 0) {
        console.log('No conversations found, creating default conversation...');
        const defaultConversation = {
          sessionId: `default-${Date.now()}`,
          title: 'Welcome Chat',
          createdAt: new Date().toISOString(),
          lastActivity: new Date().toISOString(),
          messageCount: 0,
          userId: currentUser?.username || currentUser?.email || 'anonymous',
          isActive: true
        };
        
        mergedConversations = [defaultConversation];
        ConversationStorageService.saveConversations(mergedConversations);
        console.log('Default conversation created:', defaultConversation);
      } else {
        const validationResult = ConversationStorageService.validateAndRepairData();
        if (validationResult.repaired) {
          console.log('Conversation data was repaired');
        }
      }
      
      setConversations(mergedConversations);
      console.log('Total conversations loaded:', mergedConversations.length);
      
      const activeId = ConversationStorageService.getActiveConversationId();
      console.log('Active conversation ID from storage:', activeId);
      
      if (activeId && mergedConversations.find(conv => conv.sessionId === activeId)) {
        setCurrentConversationId(activeId);
        console.log('Set active conversation:', activeId);
      } else if (mergedConversations.length > 0) {
        setCurrentConversationId(mergedConversations[0].sessionId);
        console.log('Set first conversation as active:', mergedConversations[0].sessionId);
      } else {
        setCurrentConversationId(null);
        console.log('No conversations to set as active');
      }
      
    } catch (err) {
      console.error('Error loading conversation history:', err);
      setError('Failed to load conversation history');
      
      const fallbackConversation = {
        sessionId: `fallback-${Date.now()}`,
        title: 'New Chat',
        createdAt: new Date().toISOString(),
        lastActivity: new Date().toISOString(),
        messageCount: 0,
        userId: currentUser?.username || currentUser?.email || 'anonymous',
        isActive: true
      };
      
      setConversations([fallbackConversation]);
      setCurrentConversationId(fallbackConversation.sessionId);
      
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversationMessages = async (sessionId) => {
    try {
      console.log('Loading messages for conversation:', sessionId);
      setError(null);
      
      // First check cache
      const cachedMessages = ConversationStorageService.getCachedConversationMessages(sessionId);
      if (cachedMessages && cachedMessages.length > 0) {
        console.log('Using cached messages:', cachedMessages.length);
        setMessages(cachedMessages);
        setTimeout(() => scrollToBottom("auto"), 100);
        return;
      }
      
      console.log('No cached messages found, attempting to fetch from API...');
      
      try {
        const response = await conversationApi.getConversationHistory(sessionId);
        
        console.log('Conversation history API response:', response);
        
        if (response.success && response.messages && response.messages.length > 0) {
          const formattedMessages = response.messages.map(msg => ({
            id: msg.id || msg.messageId || Date.now() + Math.random(),
            content: msg.content || msg.message,
            role: msg.role || msg.sender,
            timestamp: new Date(msg.timestamp || msg.createdAt || Date.now()),
            intent: msg.intent,
            ticketId: msg.ticketId,
            executionArn: msg.executionArn
          }));
          
          console.log('Formatted messages from API:', formattedMessages);
          
          setMessages(formattedMessages);
          ConversationStorageService.cacheConversationMessages(sessionId, formattedMessages);
          setTimeout(() => scrollToBottom("auto"), 100);
          updateConversationActivity(sessionId, formattedMessages.length);
          
          console.log('Successfully loaded messages from API:', formattedMessages.length);
        } else if (response.success && (!response.messages || response.messages.length === 0)) {
          console.log('No messages found for this conversation in API');
          setMessages([]);
        } else {
          console.log('API response unsuccessful or error:', response);
          setMessages([]);
        }
      } catch (apiError) {
        console.log('API call failed, continuing with empty messages:', apiError.message);
        setMessages([]);
      }
    } catch (err) {
      console.error('Error loading conversation messages:', err);
      setMessages([]);
      if (err.message && !err.message.includes('API') && !err.message.includes('network')) {
        setError(`Failed to load conversation history: ${err.message}`);
      }
    }
  };

  const loadTasksForConversation = async (sessionId) => {
    if (!sessionId) {
      setCurrentConversationTasks([]);
      return;
    }
    
    try {
      setLoadingTasks(true);
      console.log('Loading tasks for conversation:', sessionId);
      
      const response = await conversationApi.getUserTasks(sessionId);
      
      console.log('Tasks API response for session:', response);
      
      if (response.success && response.tasks) {
        const formattedTasks = response.tasks.map(task => ({
          ticketId: task.ticketId,
          title: task.title || task.details?.description || 'Untitled Task',
          status: task.status || 'CREATED',
          createdAt: task.createdAt,
          updatedAt: task.updatedAt,
          description: task.description || task.details?.description || 'No description available',
          details: task.details || {},
          sessionId: task.sessionId,
          executionArn: task.executionArn,
          priority: task.priority || task.details?.priority || 'medium'
        }));
        
        setCurrentConversationTasks(formattedTasks);
        console.log('Successfully loaded tasks for conversation:', formattedTasks.length);
      } else if (response.success && (!response.tasks || response.tasks.length === 0)) {
        console.log('No tasks found for this conversation');
        setCurrentConversationTasks([]);
      } else {
        console.warn('Unsuccessful response or error:', response);
        setCurrentConversationTasks([]);
        
        if (!response.success && response.error) {
          setError(`Failed to load tasks: ${response.error}`);
        }
      }
    } catch (err) {
      console.error('Error loading tasks for conversation:', err);
      setError(`Failed to load tasks: ${err.message}`);
      setCurrentConversationTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const toggleConversationsPanel = () => {
    setConversationsPanelExpanded(!conversationsPanelExpanded);
  };

  const createNewConversation = () => {
    setNewConversationTitle('');
    setShowNewConversationModal(true);
  };

  const confirmNewConversation = async () => {
    const title = newConversationTitle.trim() || `New Conversation ${new Date().toLocaleDateString()}`;
    const userId = currentUser?.username || currentUser?.email || 'anonymous';
    
    const tempSessionId = `temp-${userId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const newConversation = {
      sessionId: tempSessionId,
      title: title,
      createdAt: new Date().toISOString(),
      lastActivity: new Date().toISOString(),
      messageCount: 0,
      userId: userId,
      isActive: true,
      isTemporary: true
    };
    
    setShowNewConversationModal(false);
    setNewConversationTitle('');
    
    const updatedConversations = [newConversation, ...conversations];
    setConversations(updatedConversations);
    ConversationStorageService.saveConversations(updatedConversations);
    
    setCurrentConversationId(tempSessionId);
    setMessages([]);
    setCurrentConversationTasks([]);
    
    setError(null);
    setSuccess(null);
    
    // Auto-collapse conversations panel after creating new conversation
    setConversationsPanelExpanded(false);
    
    setTimeout(() => {
      messageInputRef.current?.focus();
    }, 100);
    
    console.log('Created new temporary conversation:', tempSessionId);
  };

  const updateConversationActivity = (sessionId, messageCount = null) => {
    const updates = {};
    if (messageCount !== null) {
      updates.messageCount = messageCount;
    }
    
    const updatedConversation = ConversationStorageService.updateConversationActivity(sessionId, updates);
    
    if (updatedConversation) {
      const updatedConversations = conversations.map(conv => 
        conv.sessionId === sessionId ? updatedConversation : conv
      );
      setConversations(updatedConversations);
    }
  };

  const deleteConversation = (sessionId, e) => {
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      const updatedConversations = ConversationStorageService.deleteConversation(sessionId);
      setConversations(updatedConversations);
      
      if (currentConversationId === sessionId) {
        setCurrentConversationId(updatedConversations.length > 0 ? updatedConversations[0].sessionId : null);
      }
    }
  };

  const handleConversationSelect = (sessionId) => {
    console.log('Switching to conversation:', sessionId);
    
    setError(null);
    setSuccess(null);
    setCurrentConversationId(sessionId);
    // Panel will auto-collapse due to useEffect
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || loading || isProcessing || !currentConversationId) return;
    
    const userMessage = {
      id: Date.now(),
      content: newMessage.trim(),
      role: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setError(null);
    setSuccess(null);
    setLoading(true);
    setIsProcessing(true);
    setProcessingMessage('Sending message...');
    
    // Auto-collapse conversations panel when sending message
    if (conversationsPanelExpanded) {
      setConversationsPanelExpanded(false);
    }
    
    setTimeout(() => scrollToBottom("auto"), 50);
    
    try {
      console.log('Sending message:', userMessage.content);
      console.log('Using session ID:', currentConversationId);
      
      setProcessingMessage('Processing your request...');
      
      const response = await conversationApi.sendMessage(
        userMessage.content, 
        currentConversationId, 
        currentUser?.username || currentUser?.email || 'anonymous'
      );
      
      console.log('Received response:', response);
      
      if (response.success) {
        let finalSessionId = currentConversationId;
        let conversationUpdated = false;
        
        if (response.sessionId && response.sessionId !== currentConversationId) {
          finalSessionId = response.sessionId;
          conversationUpdated = true;
          
          console.log(`Updating session ID from ${currentConversationId} to ${finalSessionId}`);
          
          const updatedConversations = conversations.map(conv => {
            if (conv.sessionId === currentConversationId) {
              return {
                ...conv,
                sessionId: finalSessionId,
                isTemporary: false,
                lastActivity: new Date().toISOString()
              };
            }
            return conv;
          });
          
          setConversations(updatedConversations);
          ConversationStorageService.saveConversations(updatedConversations);
          setCurrentConversationId(finalSessionId);
          
          if (currentConversationId.startsWith('temp-')) {
            ConversationStorageService.clearConversationMessages(currentConversationId);
          }
        }
        
        const assistantMessage = {
          id: response.responseMessageId || Date.now() + 1,
          content: response.response || 'Message processed successfully',
          role: 'assistant',
          timestamp: new Date(response.responseTimestamp || new Date()),
          intent: response.intent,
          ticketId: response.ticketId,
          executionArn: response.executionArn
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        
        const updatedMessages = [...messages, userMessage, assistantMessage];
        ConversationStorageService.cacheConversationMessages(finalSessionId, updatedMessages);
        updateConversationActivity(finalSessionId, updatedMessages.length);
        
        if (response.ticketId) {
          setSuccess(`Task created successfully! Ticket ID: ${response.ticketId}`);
          setTimeout(() => loadTasksForConversation(finalSessionId), 1000);
        }
        
        console.log('Message exchange completed successfully:', {
          originalSessionId: currentConversationId,
          finalSessionId,
          conversationUpdated,
          messageCount: updatedMessages.length,
          hasTicket: !!response.ticketId
        });
        
      } else {
        throw new Error(response.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      const errorMessage = `Failed to send message: ${err.message}`;
      setError(errorMessage);
      
      const errorChatMessage = {
        id: Date.now() + 2,
        content: 'I apologize, but I encountered an error processing your message. Please try again.',
        role: 'assistant',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorChatMessage]);
      
      setTimeout(() => scrollToBottom("auto"), 100);
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setProcessingMessage('');
      
      setTimeout(() => messageInputRef.current?.focus(), 100);
    }
  };

  const handleExecuteTask = async (task) => {
    setSelectedTask(task);
    setTaskExecutionResult(null);
    setShowExecuteModal(true);
  };

  const confirmTaskExecution = async () => {
    if (!selectedTask) return;
    
    try {
      setExecutingTask(true);
      const response = await conversationApi.executeTask(selectedTask.ticketId);
      
      if (response.success) {
        setTaskExecutionResult({
          success: true,
          message: `Task execution started successfully for: ${selectedTask.title}`,
          taskTitle: selectedTask.title
        });
        
        // Refresh tasks for the current conversation
        setTimeout(() => loadTasksForConversation(currentConversationId), 1000);
      } else {
        throw new Error(response.error || 'Failed to execute task');
      }
    } catch (err) {
      console.error('Error executing task:', err);
      setTaskExecutionResult({
        success: false,
        message: `Failed to execute task: ${err.message}`,
        taskTitle: selectedTask.title
      });
    } finally {
      setExecutingTask(false);
    }
  };

  const closeExecuteModal = () => {
    setShowExecuteModal(false);
    setSelectedTask(null);
    
    // Show success/error message in main chat if there was a result
    if (taskExecutionResult) {
      if (taskExecutionResult.success) {
        setSuccess(taskExecutionResult.message);
      } else {
        setError(taskExecutionResult.message);
      }
    }
    
    setTaskExecutionResult(null);
    setExecutingTask(false);
  };

  const handleSettingsChange = (newSettings) => {
    if (newSettings.dataCleared) {
      setConversations([]);
      setCurrentConversationId(null);
      setMessages([]);
      setCurrentConversationTasks([]);
      loadConversationHistory();
    } else {
      setTaskFilter(newSettings.taskFilter);
    }
  };

  const refreshConversations = async () => {
    console.log('Refreshing conversations...');
    await loadConversationHistory();
  };

  const getTaskStatusBadge = (status) => {
    const statusConfig = {
      'CREATED': { variant: 'secondary', icon: FaClock },
      'EXECUTING': { variant: 'primary', icon: FaPlay },
      'PROCESSING': { variant: 'info', icon: FaPlay },
      'COMPLETED': { variant: 'success', icon: FaCheckCircle },
      'FAILED': { variant: 'danger', icon: FaExclamationTriangle }
    };
    
    const config = statusConfig[status] || statusConfig['CREATED'];
    const IconComponent = config.icon;
    
    return (
      <Badge bg={config.variant} className="d-flex align-items-center gap-1">
        <IconComponent size={10} />
        {status}
      </Badge>
    );
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(conversationSearch.toLowerCase())
  );

  const filteredTasks = currentConversationTasks.filter(task => {
    if (taskFilter === 'all') return true;
    return task.status.toLowerCase() === taskFilter.toLowerCase();
  });

  const getCurrentConversation = () => {
    return conversations.find(conv => conv.sessionId === currentConversationId);
  };

  return (
    <div className="agent-chat-page">
      <Container fluid className="agent-chat-main-container">
        <Row className="agent-chat-row">
          {/* LEFT COLUMN: Conversations Panel */}
          <Col 
            className={`conversations-column ${conversationsPanelExpanded ? 'expanded' : 'collapsed'}`}
            style={{
              minWidth: conversationsPanelExpanded ? '300px' : '60px',
              maxWidth: conversationsPanelExpanded ? '350px' : '60px',
              flex: conversationsPanelExpanded ? '0 0 320px' : '0 0 60px',
              transition: 'all 0.3s ease'
            }}
          >
            <div className="conversations-panel">
              <div className="conversations-header">
                {conversationsPanelExpanded ? (
                  <>
                    <h6 className="conversations-title">
                      <FaComments className="me-2" />
                      Conversations ({conversations.length})
                    </h6>
                    <div className="conversations-controls">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={createNewConversation}
                        title="New Conversation"
                      >
                        <FaPlus />
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={refreshConversations}
                        title="Refresh"
                      >
                        <FaSync />
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => setShowSettingsModal(true)}
                        title="Settings"
                      >
                        <FaCog />
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={toggleConversationsPanel}
                        title="Collapse"
                      >
                        <FaCompress />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="collapsed-controls">
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={toggleConversationsPanel}
                      title="Expand Conversations"
                      className="w-100 mb-2"
                    >
                      <FaExpand />
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={createNewConversation}
                      title="New Conversation"
                      className="w-100"
                    >
                      <FaPlus />
                    </Button>
                  </div>
                )}
              </div>
              
              {conversationsPanelExpanded && (
                <>
                  <div className="conversations-search">
                    <InputGroup size="sm">
                      <InputGroup.Text>
                        <FaSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search conversations..."
                        value={conversationSearch}
                        onChange={(e) => setConversationSearch(e.target.value)}
                      />
                    </InputGroup>
                  </div>
                  
                  <div className="conversations-list">
                    {loadingConversations ? (
                      <div className="loading-state">
                        <Spinner size="sm" className="me-2" />
                        Loading conversations...
                      </div>
                    ) : filteredConversations.length === 0 ? (
                      <div className="empty-state">
                        <FaComments size={32} className="mb-2 opacity-50" />
                        <p className="mb-2">No conversations yet</p>
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={createNewConversation}
                        >
                          <FaPlus className="me-1" />
                          Start New Chat
                        </Button>
                      </div>
                    ) : (
                      <div className="conversations-scroll">
                        {filteredConversations.map((conversation) => (
                          <div
                            key={conversation.sessionId}
                            className={`conversation-item ${currentConversationId === conversation.sessionId ? 'active' : ''}`}
                            onClick={() => handleConversationSelect(conversation.sessionId)}
                          >
                            <div className="conversation-content">
                              <div className="conversation-info">
                                <h6 className="conversation-title">
                                  {conversation.title}
                                  {conversation.isTemporary && (
                                    <small className="text-muted ms-2">(temp)</small>
                                  )}
                                </h6>
                                <small className="conversation-meta">
                                  {formatDate(conversation.lastActivity)}
                                  {conversation.messageCount > 0 && (
                                    <span className="ms-2">• {conversation.messageCount} messages</span>
                                  )}
                                </small>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="conversation-delete-btn"
                                onClick={(e) => deleteConversation(conversation.sessionId, e)}
                              >
                                <FaTrash size={10} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Col>

          {/* MIDDLE COLUMN: Chat Area */}
          <Col className="chat-column">
            <div className="chat-container">
              {/* Chat Header - Removed "Back to Home" button */}
              <div className="chat-header">
                <div className="chat-header-content">
                  <div className="chat-header-left">
                    <FaRobot className="me-2" size={24} />
                    <div>
                      <h4 className="chat-title">
                        {getCurrentConversation()?.title || MAIN_AGENT.name}
                      </h4>
                      {currentConversationId && (
                        <small className="chat-session-id">
                          Session: {currentConversationId.substring(currentConversationId.lastIndexOf('-') + 1)}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="chat-header-right">
                    {!conversationsPanelExpanded && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={toggleConversationsPanel}
                      >
                        <FaComments className="me-1" />
                        Show Conversations
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Alerts */}
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              
              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                  {success}
                </Alert>
              )}

              {/* Processing Alert */}
              {isProcessing && (
                <Alert variant="info" className="d-flex align-items-center">
                  <Spinner size="sm" className="me-2" />
                  {processingMessage || 'Processing your request...'}
                </Alert>
              )}

              {/* Chat Messages Area */}
              <div className="chat-messages-area">
                {!currentConversationId ? (
                  <div className="chat-empty-state">
                    <FaRobot size={64} className="text-muted mb-4" />
                    <h3>Welcome to {MAIN_AGENT.name}</h3>
                    <p className="text-muted text-center mb-4">
                      Select a conversation from the sidebar or create a new one to get started.
                    </p>
                    <Button variant="primary" onClick={createNewConversation}>
                      <FaPlus className="me-2" />
                      Start New Conversation
                    </Button>
                  </div>
                ) : (
                  <div className="chat-messages-container" ref={messagesContainerRef}>
                    {messages.length === 0 ? (
                      <div className="chat-welcome">
                        <FaRobot size={48} className="text-muted mb-3" />
                        <h5>Start your conversation</h5>
                        <p className="text-muted">
                          Ask me to create new agents, manage tasks, or get assistance 
                          with your development workflow.
                        </p>
                      </div>
                    ) : (
                      <div className="messages-list">
                        {messages.map((message) => (
                          <div 
                            key={message.id} 
                            className={`message ${message.role} ${message.isError ? 'error' : ''}`}
                          >
                            <div className="message-avatar">
                              {message.role === 'user' ? (
                                <FaUser />
                              ) : (
                                <FaRobot />
                              )}
                            </div>
                            <div className="message-content">
                              <div className="message-header">
                                <span className="message-sender">
                                  {message.role === 'user' ? 'You' : MAIN_AGENT.name}
                                </span>
                                <span className="message-time">
                                  {formatTimestamp(message.timestamp)}
                                </span>
                              </div>
                              <div className="message-text">
                                {message.content}
                              </div>
                              {message.intent && message.intent !== 'GENERAL_QUESTION' && (
                                <div className="message-meta mt-2">
                                  <Badge bg="info" size="sm">
                                    Intent: {message.intent}
                                  </Badge>
                                  {message.ticketId && (
                                    <Badge bg="success" size="sm" className="ms-2">
                                      Task: {message.ticketId.substring(0, 8)}...
                                    </Badge>
                                  )}
                                  {message.executionArn && (
                                    <Badge bg="secondary" size="sm" className="ms-2">
                                      Execution: {message.executionArn.split(':').pop().substring(0, 8)}...
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        {loading && (
                          <div className="message assistant">
                            <div className="message-avatar">
                              <FaRobot />
                            </div>
                            <div className="message-content">
                              <div className="typing-indicator">
                                <Spinner size="sm" className="me-2" />
                                {processingMessage || `${MAIN_AGENT.name} is thinking...`}
                              </div>
                            </div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Message Input */}
              {currentConversationId && (
                <div className="chat-input-area">
                  <Form onSubmit={handleSendMessage} className="d-flex">
                    <Form.Control
                      ref={messageInputRef}
                      type="text"
                      placeholder={`Send a message to ${MAIN_AGENT.name}...`}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onFocus={handleMessageInputFocus}
                      disabled={loading || isProcessing}
                      className="message-input"
                    />
                    <Button 
                      type="submit" 
                      variant="primary"
                      disabled={loading || isProcessing || !newMessage.trim()}
                      className="send-button"
                    >
                      {loading || isProcessing ? (
                        <Spinner size="sm" />
                      ) : (
                        <FaPaperPlane />
                      )}
                    </Button>
                  </Form>
                </div>
              )}
            </div>
          </Col>

          {/* RIGHT COLUMN: Tasks Panel */}
          <Col 
            className="tasks-column"
            style={{
              minWidth: '280px',
              maxWidth: '320px',
              flex: '0 0 300px'
            }}
          >
            <div className="tasks-panel">
              <div className="tasks-header">
                <div className="tasks-header-content">
                  <h6 className="tasks-title">
                    <FaTasks className="me-2" />
                    Tasks ({currentConversationTasks.length})
                  </h6>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => loadTasksForConversation(currentConversationId)}
                    disabled={loadingTasks}
                  >
                    {loadingTasks ? <Spinner size="sm" /> : <FaSync />}
                  </Button>
                </div>
                
                <Form.Select 
                  size="sm" 
                  value={taskFilter} 
                  onChange={(e) => setTaskFilter(e.target.value)}
                  className="mt-2"
                >
                  <option value="all">All Tasks</option>
                  <option value="created">Created</option>
                  <option value="executing">Executing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </Form.Select>
              </div>
              
              <div className="tasks-content">
                {!currentConversationId ? (
                  <div className="tasks-empty-state">
                    <FaTasks size={24} className="mb-2 opacity-50" />
                    <p>Select a conversation to view tasks</p>
                  </div>
                ) : loadingTasks ? (
                  <div className="tasks-loading-state">
                    <Spinner size="sm" className="me-2" />
                    Loading tasks...
                  </div>
                ) : filteredTasks.length === 0 ? (
                  <div className="tasks-empty-state">
                    <FaTasks size={24} className="mb-2 opacity-50" />
                    <p>
                      {taskFilter === 'all' 
                        ? 'No tasks yet' 
                        : `No ${taskFilter} tasks`}
                    </p>
                    {taskFilter === 'all' && (
                      <small>Tasks appear when created through chat</small>
                    )}
                  </div>
                ) : (
                  <div className="tasks-list">
                    {filteredTasks.map((task) => (
                      <div key={task.ticketId} className="task-item">
                        <div className="task-header">
                          <h6 className="task-title">
                            {task.title}
                          </h6>
                          <div className="task-status">
                            {getTaskStatusBadge(task.status)}
                          </div>
                        </div>
                        <p className="task-description">
                          {task.description || task.details?.description || 'No description available'}
                        </p>
                        <div className="task-meta">
                          <small>
                            <strong>Created:</strong> {new Date(task.createdAt).toLocaleDateString()}
                          </small>
                          {task.priority && (
                            <small className="d-block">
                              <strong>Priority:</strong> {task.priority}
                            </small>
                          )}
                        </div>
                        <div className="task-actions">
                          {(task.status === 'CREATED' || task.status === 'FAILED') && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleExecuteTask(task)}
                            >
                              <FaPlay size={8} className="me-1" /> Execute
                            </Button>
                          )}
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                              console.log('View task details:', task);
                            }}
                          >
                            <FaHistory size={8} className="me-1" /> Details
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Custom Modals */}
      
      {/* New Conversation Modal */}
      <CustomModal
        show={showNewConversationModal}
        onHide={() => {
          setShowNewConversationModal(false);
          setNewConversationTitle('');
        }}
        title="Start New Conversation"
        footer={
          <>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowNewConversationModal(false);
                setNewConversationTitle('');
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={confirmNewConversation}
            >
              <FaPlus className="me-2" />
              Start Conversation
            </Button>
          </>
        }
      >
        <Form onSubmit={(e) => {
          e.preventDefault();
          confirmNewConversation();
        }}>
          <Form.Group className="mb-3">
            <Form.Label>Conversation Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter a title for this conversation..."
              value={newConversationTitle}
              onChange={(e) => setNewConversationTitle(e.target.value)}
              autoFocus
            />
            <Form.Text className="text-muted">
              If left empty, a default title will be generated.
            </Form.Text>
          </Form.Group>
        </Form>
      </CustomModal>

      {/* Task Execution Modal */}
      <CustomModal
        show={showExecuteModal}
        onHide={closeExecuteModal}
        title={taskExecutionResult ? 'Task Execution Result' : 'Confirm Task Execution'}
        size="lg"
        footer={
          taskExecutionResult ? (
            <Button variant="primary" onClick={closeExecuteModal}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={closeExecuteModal}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={confirmTaskExecution}
                disabled={executingTask}
              >
                {executingTask ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Executing...
                  </>
                ) : (
                  <>
                    <FaPlay className="me-2" />
                    Execute Task
                  </>
                )}
              </Button>
            </>
          )
        }
      >
        {taskExecutionResult ? (
          // Show execution result
          <div className="execution-result">
            <div className={`alert ${taskExecutionResult.success ? 'alert-success' : 'alert-danger'}`}>
              <div className="d-flex align-items-center">
                {taskExecutionResult.success ? (
                  <FaCheckCircle className="me-2" size={24} />
                ) : (
                  <FaExclamationTriangle className="me-2" size={24} />
                )}
                <div>
                  <h6 className="mb-1">
                    {taskExecutionResult.success ? 'Success!' : 'Error'}
                  </h6>
                  <p className="mb-0">{taskExecutionResult.message}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Show confirmation dialog
          selectedTask && (
            <>
              <p>Are you sure you want to execute this task?</p>
              <div className="task-summary p-3 bg-light rounded">
                <h6>{selectedTask.title}</h6>
                <p className="mb-2">{selectedTask.description || selectedTask.details?.description || 'No description available'}</p>
                <div className="mt-2">
                  {getTaskStatusBadge(selectedTask.status)}
                  <Badge bg="secondary" className="ms-2">
                    ID: {selectedTask.ticketId.substring(0, 8)}...
                  </Badge>
                  {selectedTask.priority && (
                    <Badge bg="info" className="ms-2">
                      Priority: {selectedTask.priority}
                    </Badge>
                  )}
                </div>
                <div className="mt-2">
                  <small className="text-muted">
                    <strong>Created:</strong> {new Date(selectedTask.createdAt).toLocaleString()}
                  </small>
                </div>
              </div>
              <div className="mt-3">
                <small className="text-muted">
                  This will start the task execution process. You'll be notified when it completes.
                </small>
              </div>
            </>
          )
        )}
      </CustomModal>

      {/* Settings Modal - Using the existing ConversationSettingsModal */}
      <ConversationSettingsModal
        show={showSettingsModal}
        onHide={() => setShowSettingsModal(false)}
        onSettingsChange={handleSettingsChange}
        backdrop={true}
        centered
      />
    </div>
  );
};

export default AgentChat;
